// PLAIN shell — branded chrome with just a heading, the copy and an optional CTA
// button. Used by win-back (WB), anniversary (AN) and the renewal-day documents
// email (REN0), where "You're all set for renewal" or pricing would be wrong.
// Logic ported verbatim from rensure-sme-smp/lib/messaging/layout.ts (plainEmailHtml); markup in the HQ welcome-email design.

import { escapeHtml, telHref } from "../format";
import { type BrandedEmailArgs, bodyRow, button, emailDocument, policyCardHtml, row } from "./chrome";

export type Cta = { label: string; url: string };

export type PlainEmailArgs = BrandedEmailArgs & {
  /** The H1 above the body copy. */
  heading: string;
  cta?: Cta;
  /** MARKETING emails must carry a one-click unsubscribe (PECR). */
  unsubscribeUrl?: string;
};

export function plainEmailHtml(args: PlainEmailArgs): string {
  const { brand, data, bodyHtml, callNumber, heading, cta } = args;
  // Win-back / anniversary templates always pass the unsubscribeUrl key (PECR); REN0 never does.
  const marketing = "unsubscribeUrl" in args;

  const ctaHtml = cta
    ? `<div style="height:8px;line-height:8px;font-size:1px;">&nbsp;</div>${button(brand, escapeHtml(cta.label), cta.url)}`
    : "";

  const helpHtml = `<p style="margin:18px 0 0;font-size:13px;line-height:21px;color:${brand.text};text-align:center;">Questions? Call our UK-based team on <a href="${telHref(callNumber)}" style="color:${brand.primary};text-decoration:none;font-weight:700;white-space:nowrap;">${escapeHtml(callNumber)}</a>.</p>`;

  // PECR: one-click opt-out on every marketing email.
  const unsubscribeHtml = args.unsubscribeUrl
    ? `<p style="margin:16px 0 0;padding-top:14px;border-top:1px solid ${brand.line};font-size:12px;line-height:19px;color:${brand.text};">You're receiving this because you previously held a policy with ${escapeHtml(brand.name)}. <a href="${args.unsubscribeUrl}" style="color:${brand.text};text-decoration:underline;">Unsubscribe from these offers</a>.</p>`
    : "";

  return emailDocument({
    brand,
    eyebrow: marketing ? "We miss you" : "You're renewed",
    heading,
    policyCard: marketing ? "" : policyCardHtml(brand, data, []),
    marketing,
    iptNote: false,
    rows: `${bodyRow(brand, bodyHtml)}
${row(`${ctaHtml}\n${helpHtml}\n${unsubscribeHtml}`, "padding:8px 36px 32px;", "pad")}`,
  });
}

/** The brand call CTA used by every win-back / anniversary email. */
export const callCta = (callNumber: string): Cta => ({
  label: `Call ${callNumber}`,
  url: telHref(callNumber),
});
